import { Component } from '@theme/component';

/**
 * A custom element that enables horizontal scrolling via mouse wheel
 *
 * @extends {Component}
 */
class HorizontalScroll extends Component {
  /**
   * @type {AbortController}
   */
  #controller = new AbortController();

  /**
   * Scroll speed multiplier. Higher values scroll faster.
   * @type {number}
   */
  #scrollSpeed = 1;

  /**
   * Whether horizontal scrolling is enabled
   * @type {boolean}
   */
  #enabled = true;

  /**
   * Whether the element is currently being dragged
   * @type {boolean}
   */
  #isDragging = false;

  /**
   * Whether a drag actually occurred (mouse moved significantly)
   * @type {boolean}
   */
  #hasDragged = false;

  /**
   * Initial X position when drag starts
   * @type {number}
   */
  #startX = 0;

  /**
   * Initial scroll position when drag starts
   * @type {number}
   */
  #startScrollLeft = 0;

  /**
   * Threshold in pixels to determine if a drag occurred
   * @type {number}
   */
  #dragThreshold = 5;

  /**
   * Active touch identifier for tracking the primary touch point
   * @type {number | null}
   */
  #activeTouchId = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    const speedAttr = this.getAttribute('scroll-speed');
    if (speedAttr) {
      const speed = parseFloat(speedAttr);
      if (!Number.isNaN(speed) && speed > 0) {
        this.#scrollSpeed = speed;
      }
    }

    const enabledAttr = this.getAttribute('enabled');
    if (enabledAttr !== null) {
      this.#enabled = enabledAttr !== 'false';
    }

    this.addEventListener('wheel', this.#handleWheel, { signal, passive: false, capture: true });
    this.addEventListener('mousedown', this.#handleMouseDown, { signal });
    this.addEventListener('click', this.#handleClick, { signal, capture: true });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    
    document.addEventListener('mousemove', this.#handleMouseMove, { signal });
    document.addEventListener('mouseup', this.#handleMouseUp, { signal });
    this.addEventListener('mouseleave', this.#handleMouseLeave, { signal });
    
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
    
    this.#updateCursor();
  }

  /**
   * Updates the cursor style based on whether horizontal scrolling is available
   */
  #updateCursor() {
    const hasHorizontalScroll = this.scrollWidth > this.clientWidth;
    if (hasHorizontalScroll) {
      this.style.cursor = 'grab';
      this.style.userSelect = 'none';
    } else {
      this.style.cursor = '';
      this.style.userSelect = '';
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles wheel events to convert vertical scrolling to horizontal scrolling
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    if (!this.#enabled) return;

    const element = this;
    const hasHorizontalScroll = element.scrollWidth > element.clientWidth;
    
    if (!hasHorizontalScroll) return;

    const rect = element.getBoundingClientRect();
    const isPointerOverElement = 
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (!isPointerOverElement) return;

    const isScrollingHorizontally = Math.abs(event.deltaX) > Math.abs(event.deltaY);
    
    if (isScrollingHorizontally) {
      return;
    }

    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    const currentScrollLeft = element.scrollLeft;
    
    if (maxScrollLeft <= 0) return;
    
    const canScrollLeft = currentScrollLeft > 0.5;
    const canScrollRight = currentScrollLeft < maxScrollLeft - 0.5;
    
    const wantsToScrollRight = event.deltaY > 0;
    const wantsToScrollLeft = event.deltaY < 0;
    
    const willScroll = 
      (wantsToScrollRight && canScrollRight) ||
      (wantsToScrollLeft && canScrollLeft);

    if (willScroll) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      const scrollAmount = event.deltaY * this.#scrollSpeed;
      const newScrollLeft = Math.max(0, Math.min(maxScrollLeft, currentScrollLeft + scrollAmount));
      
      element.scrollLeft = newScrollLeft;
    }
  };

  /**
   * Sets the scroll speed multiplier
   * @param {number} speed - The scroll speed multiplier (must be > 0)
   */
  setScrollSpeed(speed) {
    if (typeof speed === 'number' && speed > 0) {
      this.#scrollSpeed = speed;
      this.setAttribute('scroll-speed', String(speed));
    }
  }

  /**
   * Gets the current scroll speed multiplier
   * @returns {number}
   */
  getScrollSpeed() {
    return this.#scrollSpeed;
  }

  /**
   * Enables or disables horizontal scrolling
   * @param {boolean} enabled - Whether to enable horizontal scrolling
   */
  setEnabled(enabled) {
    this.#enabled = enabled;
    if (enabled) {
      this.removeAttribute('enabled');
    } else {
      this.setAttribute('enabled', 'false');
    }
  }

  /**
   * Gets whether horizontal scrolling is enabled
   * @returns {boolean}
   */
  getEnabled() {
    return this.#enabled;
  }

  /**
   * Handles mouse down event to start dragging
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    if (!this.#enabled) return;
    if (event.button !== 0) return;

    const element = this;
    const hasHorizontalScroll = element.scrollWidth > element.clientWidth;
    
    if (!hasHorizontalScroll) return;

    this.#isDragging = true;
    this.#hasDragged = false;
    this.#startX = event.clientX;
    this.#startScrollLeft = element.scrollLeft;
    
    this.style.cursor = 'grabbing';
    this.style.userSelect = 'none';
    
    event.preventDefault();
  };

  /**
   * Handles mouse move event during dragging
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (!this.#isDragging) return;

    const element = this;
    const deltaX = this.#startX - event.clientX;
    
    if (Math.abs(deltaX) > this.#dragThreshold) {
      this.#hasDragged = true;
    }
    
    const newScrollLeft = this.#startScrollLeft + deltaX;
    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    
    element.scrollLeft = Math.max(0, Math.min(maxScrollLeft, newScrollLeft));
    
    event.preventDefault();
  };

  /**
   * Handles mouse up event to stop dragging
   */
  #handleMouseUp = () => {
    if (!this.#isDragging) return;
    
    const hadDragged = this.#hasDragged;
    this.#isDragging = false;
    
    if (hadDragged) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#hasDragged = false;
        });
      });
    }
    
    this.#updateCursor();
  };

  /**
   * Handles mouse leave event to stop dragging
   */
  #handleMouseLeave = () => {
    if (this.#isDragging) {
      const hadDragged = this.#hasDragged;
      this.#isDragging = false;
      
      if (hadDragged) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#hasDragged = false;
          });
        });
      }
      
      this.#updateCursor();
    }
  };

  /**
   * Handles click events to prevent clicks after dragging
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (this.#hasDragged) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  };

  /**
   * Handles keyboard events for accessibility navigation
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (!this.#enabled) return;

    const hasHorizontalScroll = this.scrollWidth > this.clientWidth;
    if (!hasHorizontalScroll) return;

    const maxScrollLeft = this.scrollWidth - this.clientWidth;
    const currentScrollLeft = this.scrollLeft;
    const scrollStep = 100;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        const newScrollLeft = Math.max(0, currentScrollLeft - scrollStep);
        this.scrollLeft = newScrollLeft;
        break;

      case 'ArrowRight':
        event.preventDefault();
        const newScrollRight = Math.min(maxScrollLeft, currentScrollLeft + scrollStep);
        this.scrollLeft = newScrollRight;
        break;

      case 'Home':
        event.preventDefault();
        this.scrollLeft = 0;
        break;

      case 'End':
        event.preventDefault();
        this.scrollLeft = maxScrollLeft;
        break;

      default:
        return;
    }
  };
}

if (!customElements.get('horizontal-scroll')) {
  customElements.define('horizontal-scroll', HorizontalScroll);
}

